import { memo, useEffect, useState } from 'react';
import { Content, Fetching, Hidden, HiddenCont } from '../../image-video-audio.styles';
import { Box, CircularProgress } from '@material-ui/core';
import { IAgencyTalentPermissionPayload, IProfileMedia } from 'shared/interfaces/IProfile';
import { CustomTypography } from 'shared/elements/CustomTypography';
import { DocumentItem } from '../../Items/Document/DocumentItem';
import { DocumentHiddenItem } from '../../Items/Document/DocumentHiddeinItem';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { profileService } from 'shared/services/profileService';
import { useQueryClient } from 'react-query';
import { isEqual } from 'lodash';
import DocumentUploadingItem from '../../Items/Document/DocumentUploadingItem';
import IMedia from 'shared/interfaces/IMedia';
interface IOutputE {
  file: File;
  id: string;
}

type DocumentContentProps = {
  profileId: string;
  isAllowed: boolean;
  fetching: boolean;
  hiddenDocumentData: IMedia[];
  specificProfileDocumentData: IProfileMedia[];
  toUpload: IOutputE[];
  uploadingProgress: number | null;
  handleCheck: (value: Partial<IMedia | IProfileMedia>) => void;
  handleAgencyTalentPermissions: (payload: IAgencyTalentPermissionPayload) => void;
};

const { updateProfileMediaSort } = profileService();

const DocumentContent = ({
  profileId,
  isAllowed,
  fetching,
  hiddenDocumentData,
  specificProfileDocumentData,
  toUpload,
  uploadingProgress,
  handleCheck,
  handleAgencyTalentPermissions,
}: DocumentContentProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const queryClient = useQueryClient();
  const [specificData, setSpecificData] = useState<IProfileMedia[]>([]);
  const [willUpdateSort, setWillUpdateSort] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  const { mutate: updateSortMutate } = updateProfileMediaSort();
  const handleOnDragStart = (event: DragEndEvent) => {
    const { active } = event;
    if (!active) return;
    setWillUpdateSort(false);
    setActiveId(specificData?.findIndex((data) => data.id === active.id));
  };

  const handleOnDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!active || !over) return;
    if (active.id !== over.id) {
      setSpecificData((prevItems) => {
        const oldIndex = prevItems.findIndex((i) => active.id === i.id);
        const newIndex = prevItems.findIndex((i) => over.id === i.id);
        const latest = arrayMove(prevItems, oldIndex, newIndex);
        return latest;
      });
    }
    setWillUpdateSort(true);
  };

  const handleDocumentCheck = (document: Partial<IProfileMedia | IMedia>) => {
    handleAgencyTalentPermissions({
      module: 'documents',
      ctaText: 'Close',
      callback: () => handleCheck(document),
    });
  };

  useEffect(() => {
    setSpecificData(specificProfileDocumentData);
  }, [specificProfileDocumentData]);

  useEffect(() => {
    if (willUpdateSort && !isEqual(specificProfileDocumentData, specificData)) {
      const updatedItems = specificData.map((item, i) => {
        return {
          id: item.id,
          sort: i,
        };
      });
      const timer = setTimeout(() => {
        updateSortMutate({ profileId, sort: updatedItems });
        queryClient.setQueriesData(['profile_media', profileId, { file_type: 'document' }], { data: specificData });

        return setWillUpdateSort(false);
      }, 500);
      return () => {
        clearTimeout(timer);
        setWillUpdateSort(false);
      };
    }
  }, [willUpdateSort]);

  return (
    <Content>
      {fetching ? (
        <Fetching>
          <CircularProgress
            style={{
              width: 20,
              height: 20,
              marginRight: 10,
            }}
          />
          Fetching Data...
        </Fetching>
      ) : specificData?.length || toUpload?.length ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleOnDragStart}
          onDragEnd={handleOnDragEnd}
        >
          <SortableContext items={specificData}>
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
              {specificData?.map((document, index) => {
                return (
                  <DocumentItem
                    key={document.id || document?.attributes?.medium_id}
                    document={document}
                    isAllowed={isAllowed}
                    handleCheck={() => handleDocumentCheck(document)}
                    index={index}
                  />
                );
              })}
              {toUpload?.map((docs) => {
                return (
                  <DocumentUploadingItem key={docs.id} uploadingProgress={uploadingProgress} name={docs.file.name} />
                );
              })}
            </Box>
          </SortableContext>
          <DragOverlay>
            {activeId !== null ? (
              <DocumentItem
                document={specificData[activeId]}
                isAllowed={isAllowed}
                handleCheck={() => {}}
                index={activeId}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <Fetching>
          <CustomTypography fontSize={18}>No Documents Selected</CustomTypography>
          {!specificProfileDocumentData?.length ? (
            <CustomTypography fontSize={15}>You can select from documents below or upload a new one.</CustomTypography>
          ) : (
            <CustomTypography fontSize={15}>
              You can upload new audio by clicking the 'ADD AUDIO' button.
            </CustomTypography>
          )}
        </Fetching>
      )}

      {hiddenDocumentData?.length ? (
        <HiddenCont>
          <Hidden>Hidden Documents</Hidden>
          <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
            {hiddenDocumentData?.map((document, index) => {
              return (
                <DocumentHiddenItem
                  key={document?.id || document?.attributes?.id || index}
                  document={document}
                  handleCheck={() => handleDocumentCheck(document)}
                  isAllowed={isAllowed}
                />
              );
            })}
          </Box>
        </HiddenCont>
      ) : (
        <></>
      )}
    </Content>
  );
};

export default memo(DocumentContent);
